/**
 * DuplicateUploadModal — tells the writer that one or more files were
 * skipped because a document with the same name already exists in the
 * library (checked live against the library's latest documents).
 *
 * SRP: pure presentation. No state, no upload logic. The shell decides
 * which names were blocked and wires the dismiss.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  dialog: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    padding: "18px 18px 16px",
    maxWidth: "420px",
    width: "100%",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  title: {
    fontFamily: fonts.display,
    fontSize: "15px",
    fontWeight: 700,
    color: inkColors.ink,
    marginBottom: "6px",
  },
  body: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
  },
  list: {
    margin: "10px 0 0",
    paddingLeft: "8px",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
    maxHeight: "180px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "3px",
  },
  fileName: {
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink2,
    wordBreak: "break-all",
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
  },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: {
    background: brandColors.primary,
    color: "#fff",
  },
});

export interface DuplicateUploadModalProps {
  /** Names of files skipped because they already exist in the library. */
  blockedNames: string[];
  /** Writer dismissed the notice. */
  onDismiss: () => void;
}

export const DuplicateUploadModal: React.FC<DuplicateUploadModalProps> = ({ blockedNames, onDismiss }) => {
  const styles = useStyles();
  const count = blockedNames.length;

  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="duplicate-upload-modal-title">
      <div className={styles.dialog} data-testid="duplicate-upload-modal">
        <div id="duplicate-upload-modal-title" className={styles.title}>
          {count === 1 ? "1 file was skipped" : `${count} files were skipped`}
        </div>
        <div className={styles.body}>
          {count === 1 ? "A document" : "These documents"} with the same name already{" "}
          {count === 1 ? "exists" : "exist"} in this library, so {count === 1 ? "it was" : "they were"} not uploaded.
          Rename the {count === 1 ? "file" : "files"} or remove the existing document first if you want to replace{" "}
          {count === 1 ? "it" : "them"}.
        </div>
        <div className={styles.list}>
          {blockedNames.map((name) => (
            <span key={name} className={styles.fileName}>
              {name}
            </span>
          ))}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnPrimary)}
            onClick={onDismiss}
            data-testid="duplicate-upload-dismiss"
          >
            Got it
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default DuplicateUploadModal;
